import {
  Button,
  ButtonGroup,
  Card,
  Input,
  Label,
  Radio,
  RadioGroupField,
  TextAreaField,
} from "@aws-amplify/ui-react"
import { EuDataActTransferSchema } from "@strtmn/sds/schemas"
import {
  type AddressObjectDto,
  type EuDataActTransferDto,
  type MachineSupaDto,
  type OrganizationDto,
  type UserDataDto,
} from "@strtmn/sds/types"
import { dateOnly, formatUnicorn, getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useContext, useEffect, useState, type PropsWithChildren } from "react"
import {
  Controller,
  FormProvider,
  get,
  useForm,
  type FieldErrors,
  type SubmitHandler,
} from "react-hook-form"
import { FaRegCalendarDays, FaRegCalendarXmark } from "react-icons/fa6"
import { useNavigate, useParams } from "react-router-dom"
import DatePicker, { type DateValueType } from "react-tailwindcss-datepicker"
import { requestTransfer } from "../../api/EuDataActApi"
import { getLatestActiveDnv } from "../../api/MachinesApi"
import { CancelButton, ErrorBanner, LoadingErrorState } from "../../components"
import VehiclePageLayout from "../../components/VehiclePageLayout"
import { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId } from "../../hooks"
import { useYupValidationResolver } from "../../hooks/useYupValidationResolver"
import { getLinkToVehicleEuDataAct } from "../../LinkUtils"
import { getCurrentUserId } from "../../Utils.AmplifyAuth"

interface PageWrapperProps extends PropsWithChildren {
  vin: string
}
function PageWrapper({ vin, children }: PageWrapperProps) {
  return (
    <VehiclePageLayout vin={vin}>
      <Card>
        <div className="section-heading mb-6">
          {I18n.get(
            "Information des Nutzers an den Dateninhaber über einen Nutzerwechsel"
          )}
        </div>
        {children}
      </Card>
    </VehiclePageLayout>
  )
}

/** @todo: extract */
interface IResponseMessage {
  type: "success" | "load-error" | "submit-error"
  message: string
}

export default function Transfer() {
  const navigate = useNavigate()
  const userPrefLang = "de" // useUserPrefLang()
  const params = useParams<{
    vin: string | undefined
  }>()
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  const [currentUserId, setCurrentUserId] = useState<string | undefined>(
    undefined
  )
  const [loading, setLoading] = useState(true)
  const [responseMessage, setResponseMessage] =
    useState<IResponseMessage | null>(null)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const now = new Date()
  const [datePickerValue, setDatePickerValue] = useState<DateValueType>({
    startDate: now,
    endDate: now,
  })

  const vin = params.vin!
  let machineFromUserCtx = undefined
  if (activeOrgId && userData) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    !isProd &&
      console.debug(
        `${machinesFromUserCtx.length} machines from userData:`,
        machinesFromUserCtx
      )
    machineFromUserCtx = machinesFromUserCtx.find(
      (supa) => supa.serial_number === vin
    )
  }

  const resolver = useYupValidationResolver(EuDataActTransferSchema)
  const methods = useForm<EuDataActTransferDto>({
    defaultValues: {
      dateOfTransfer: datePickerValue?.startDate?.toISOString() ?? "UNKNOWN",
    },
    resolver,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = methods

  const onDatePickerChange = (newValue: DateValueType) => {
    console.log("onDatePickerChange:", newValue)
    newValue?.startDate?.setHours(0, 0, 0, 0)
    newValue?.endDate?.setHours(23, 59, 59, 0)
    setDatePickerValue(newValue)
  }

  useEffect(() => {
    !isProd && console.debug("> transfer.useEffect (load data)")

    const fetchAllData = async () => {
      !isProd && console.debug("> transfer.fetchAllData")
      setLoading(true)
      setCurrentUserId(undefined)
      setResponseMessage(null)
      try {
        const myCurrentUserId = await getCurrentUserId()
        setCurrentUserId(myCurrentUserId)
        setValue("userId", myCurrentUserId)
      } catch (err) {
        console.error("Error:", err)
        setResponseMessage({
          type: "load-error",
          message: getErrorMessage(err),
        })
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [])

  if (loading || responseMessage?.type === "load-error") {
    return (
      <PageWrapper vin={vin}>
        <LoadingErrorState
          isLoading={loading}
          loadingMessage={I18n.get("Lade Maschinendaten, bitte warten...")}
          isError={!!(responseMessage?.type === "load-error")}
          errorMessage={`${I18n.get("Fehler")}: ${I18n.get(responseMessage?.message!)}`}
        />
      </PageWrapper>
    )
  }
  if (!machineFromUserCtx) {
    return (
      <PageWrapper vin={vin}>
        <ErrorBanner
          error={`${I18n.get("Fehler")}: ${I18n.get("Keine Maschinendaten geladen.")}`}
        />
      </PageWrapper>
    )
  }

  const loadedUserData: UserDataDto = userData!
  const loadedMachine: MachineSupaDto = machineFromUserCtx
  !isProd && console.log("loadedMachine =", loadedMachine)
  const latestActiveDnvTermsVersion =
    getLatestActiveDnv(loadedMachine)?.terms_version
  !isProd &&
    console.debug("latestActiveDnvTermsVersion:", latestActiveDnvTermsVersion)
  const activeTerms = loadedMachine.EuDataActTerms?.filter(
    (t) =>
      t.terms_version === latestActiveDnvTermsVersion && t.cancel_date === null
  )
  let dateOfTerms: string
  if (!activeTerms || activeTerms.length === 0) {
    // If the form would only be for machines with active terms,
    // then this would be an error in the process / backend.
    console.warn("⚡⚡⚡ Machine has no active EU Data Act terms.")
    dateOfTerms = "[GOT NO ACTIVE TERMS]"
  } else {
    dateOfTerms = dateOnly(now.toISOString())
    for (const terms of activeTerms) {
      const tAcceptDate = dateOnly(terms.accept_date)
      if (tAcceptDate < dateOfTerms) {
        dateOfTerms = tAcceptDate
      }
    }
  }

  const allAddresses: { orgName: string; address: AddressObjectDto }[] = []
  loadedUserData.Organizations.forEach((o: OrganizationDto) => {
    const a: AddressObjectDto =
      typeof o.address === "string"
        ? (JSON.parse(o.address) as AddressObjectDto)
        : o.address
    allAddresses.push({ orgName: o.name, address: a })
  })
  const hasExactlyOneAddress = allAddresses.length === 1

  const userAddressValue = allAddresses
    .flatMap(({ orgName, address }) => {
      const lines: string[] = []
      if (orgName) {
        lines.push(orgName)
      }
      if (address) {
        console.log("a= aaaa ====", address)
        // If street is there, add a line. Number may be empty.
        if (address.street) {
          lines.push(`${address.street} ${address.number}`.trim())
        }
        if (address.country || address.zip || address.city) {
          lines.push(`${address.country} ${address.zip} ${address.city}`.trim())
        }
      }
      if (lines.length > 0) {
        return lines.join("\n")
      }
      return []
    })
    .join("\n\n")

  const userNameList: string[] = []
  if (hasExactlyOneAddress && allAddresses[0]?.orgName) {
    userNameList.push(allAddresses[0]?.orgName.trim())
    userNameList.push("/")
  }
  loadedUserData.first_name &&
    userNameList.push(loadedUserData.first_name.trim())
  loadedUserData.last_name && userNameList.push(loadedUserData.last_name.trim())
  const userName = userNameList.join(" ")

  const onSubmit: SubmitHandler<any> = async (data: EuDataActTransferDto) => {
    !isProd && console.debug("> onSubmit")
    setIsFormSubmitting(true)
    !isProd && console.log("onSubmit: data =", data)
    setResponseMessage(null)
    try {
      await requestTransfer(data)
      navigate(
        getLinkToVehicleEuDataAct({ vin: loadedMachine.serial_number }),
        {
          replace: true,
          state: {
            successMessage: I18n.get(
              "Die Anfrage zum Nutzerwechsel wurde versendet."
            ),
          },
        }
      )
    } catch (err) {
      console.error("Could not submit the form:", getErrorMessage(err))
      setResponseMessage({
        type: "submit-error",
        message: getErrorMessage(err),
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const onError = (_errors: FieldErrors) => {
    !isProd && console.log("Validation failed: onError", _errors)
    setError("root.clientError", {
      type: "validate",
      message: I18n.get(
        "Das Formular enthält ungültige Eingaben. Beachten Sie die Fehlermeldungen."
      ),
    })
  }

  return (
    <PageWrapper vin={vin}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="max-w-screen-md"
        >
          <div className="text-center">{I18n.get("zwischen")}</div>
          <div>
            1. {userName}
            {/* [{I18n.get("Name des Nutzers")}] */}
          </div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "Nutzer" genannt')} -
          </div>
          <div className="text-center">{I18n.get("und")}</div>
          <div>
            2. {watch("newUsersName")} [{I18n.get("Name des neuen Nutzers")}]
          </div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "neuer Nutzer" genannt')} -
          </div>
          <div>&nbsp;</div>
          <div>
            {I18n.get(
              "Hiermit informiere ich B. Strautmann & Söhne GmbH u. Co. KG als den Dateninhaber über den"
            )}
            <RadioGroupField size="small" name="pastOrFuture" legend="">
              <Radio {...register("pastOrFuture")} value="past">
                erfolgten Nutzerwechsel
              </Radio>
              <Radio {...register("pastOrFuture")} value="future">
                zu erfolgenden Nutzerwechsel
              </Radio>
            </RadioGroupField>
            {errors.pastOrFuture && (
              <span className="error">
                {I18n.get(errors.pastOrFuture.message!)}
              </span>
            )}
            {formatUnicorn(
              I18n.get(
                "und die damit einhergehende Rechteübertragung nach Ziffer 5.1. der zwischen mir und dem Dateninhaber am {dateOfCurrentTermsAccept} geschlossenen Datennutzungsvereinbarung."
              ),
              {
                dateOfCurrentTermsAccept: new Date(
                  dateOfTerms
                ).toLocaleDateString(userPrefLang, { dateStyle: "long" }),
              }
            )}
            <input
              type="hidden"
              {...register("dateOfTerms")}
              value={dateOfTerms}
            />
            {errors.dateOfTerms && (
              <span className="error">
                {I18n.get(errors.dateOfTerms.message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>
              {I18n.get("Der Nutzerwechsel erfolgt/erfolgte am")}:<br />
              <Controller
                name="dateOfTransfer"
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => {
                  // console.log("⚡⚡⚡ field =", field)
                  return (
                    <DatePicker
                      {...field}
                      inputName={field.name}
                      // inputClassName="w-full rounded-md focus:ring-0 font-normal bg-blue-300 placeholder:text-blue-100 text-white dark:bg-blue-900 dark:placeholder:text-blue-100"
                      inputClassName="p-0 text-gray-700 outline-none border-none"
                      // containerClassName="relative mt-8"
                      containerClassName="w-auto relative px-3 py-2 datepicker-container border-2 inline"
                      // toggleClassName="absolute bg-blue-300 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                      toggleClassName="mr-0"
                      asSingle={true}
                      i18n={userPrefLang || undefined}
                      displayFormat={I18n.get("DD.MM.YYYY")}
                      onChange={(newValue) => {
                        onDatePickerChange(newValue)
                        field.onChange(newValue?.startDate)
                      }}
                      readOnly={true}
                      required={true}
                      startFrom={datePickerValue?.startDate}
                      startWeekOn="mon"
                      toggleIcon={(open: boolean) =>
                        open ? (
                          <FaRegCalendarDays className="icon inline" />
                        ) : (
                          <FaRegCalendarXmark className="icon inline" />
                        )
                      }
                      value={datePickerValue}
                    />
                  )
                }}
              />
            </Label>
            {errors.dateOfTransfer && (
              <span className="error">
                {I18n.get(errors.dateOfTransfer.message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>
              {I18n.get("Der neue Nutzer ist")}
              <Input
                {...register("newUsersName")}
                size="small"
                placeholder={I18n.get("Name des neuen Nutzers")}
              />
            </Label>
            {errors.newUsersName && (
              <span className="error">
                {I18n.get(errors.newUsersName.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Anschrift des neuen Nutzers")}
              <TextAreaField
                {...register("newUsersAddress")}
                size="small"
                resize="vertical"
                label={I18n.get("Anschrift des neuen Nutzers")}
                labelHidden={true}
              />
            </Label>
            {errors.newUsersAddress && (
              <span className="error">
                {I18n.get(errors.newUsersAddress.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("E-Mail-Adresse des neuen Nutzers")}
              <Input
                {...register("newUsersEmail")}
                size="small"
                placeholder={I18n.get("E-Mail-Adresse des neuen Nutzers")}
              />
            </Label>
            {errors.newUsersEmail && (
              <span className="error">
                {I18n.get(errors.newUsersEmail.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Telefonnummer des neuen Nutzers")}
              <Input
                {...register("newUsersPhone")}
                size="small"
                placeholder={I18n.get("Telefonnummer des neuen Nutzers")}
              />
            </Label>
            {errors.newUsersPhone && (
              <span className="error">
                {I18n.get(errors.newUsersPhone.message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Maschinennummer")}</Label>
            <br />
            {vin}
            <input
              type="hidden"
              {...register("machineSerialNumber")}
              value={vin}
            />
            {get(errors, "machineSerialNumber") && (
              <span className="error">
                {I18n.get(get(errors, "machineSerialNumber").message)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Name des Nutzers")}</Label>
            <br />
            {userName}
            <input type="hidden" {...register("userName")} value={userName} />
            {errors.userName && (
              <span className="error">
                {I18n.get(errors.userName.message!)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Anschrift des Nutzers")}
              <TextAreaField
                {...register("userAddress")}
                size="small"
                resize="vertical"
                rows={5}
                label={I18n.get("Anschrift des Nutzers")}
                labelHidden={true}
                defaultValue={userAddressValue}
              />
            </Label>
            {get(errors, "userAddress") && (
              <span className="error">
                {I18n.get(get(errors, "userAddress").message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Datum")}</Label>
            <br />
            {now.toLocaleDateString(userPrefLang, { dateStyle: "long" })}
            <input
              type="hidden"
              {...register("dateOfRequest")}
              value={dateOnly(now.toISOString())}
            />
            {get(errors, "dateOfRequest") && (
              <span className="error">
                {I18n.get(get(errors, "dateOfRequest").message)}
              </span>
            )}
          </div>

          <input type="hidden" {...register("userId")} value={currentUserId} />
          {get(errors, "userId") && (
            <span className="error">
              {I18n.get(get(errors, "userId").message)}
            </span>
          )}

          {get(errors, "root.clientError") && (
            <ErrorBanner
              error={I18n.get(get(errors, "root.clientError").message)}
            />
          )}

          {responseMessage?.type === "submit-error" && (
            <ErrorBanner error={I18n.get(responseMessage.message)} />
          )}

          <ButtonGroup className="buttons !mt-4" size="small">
            <Button
              type="submit"
              variation="primary"
              isDisabled={isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Anfrage wird gesendet...")}
            >
              {I18n.get("Anfrage senden")}
            </Button>

            <CancelButton
              href={getLinkToVehicleEuDataAct({
                vin: loadedMachine.serial_number,
              })}
              title="Abbrechen"
              isDisabled={isFormSubmitting}
            />
          </ButtonGroup>
        </form>
      </FormProvider>
    </PageWrapper>
  )
}
